import { memo, useMemo } from 'react';
import { Avatar } from '@mui/material';

import { isNumeric, stringToColor } from '@cutwise/utils';

import { OnlineBadge } from './UserAvatar.styles';

type UserAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

enum AvatarSize {
  Small = 16,
  Medium = 24,
  Large = 32,
  ExtraLarge = 48,
}

const AVATAR_SIZE_MAP: Record<UserAvatarSize, number> = {
  sm: AvatarSize.Small,
  md: AvatarSize.Medium,
  lg: AvatarSize.Large,
  xl: AvatarSize.ExtraLarge,
};

interface UserAvatarProps {
  username: string;
  size?: UserAvatarSize | string;
  isOnline?: boolean;
}

function UserAvatar(props: UserAvatarProps) {
  const { username, size = AvatarSize.ExtraLarge, isOnline = false } = props;

  const avatarSizeStyles = useMemo(() => {
    if (isNumeric(size)) {
      return {
        width: size,
        height: size,
        lineHeight: `${size}px`,
        fontSize: `${(size as number) / AvatarSize.Medium}rem`,
      };
    }

    const predefinedSize = AVATAR_SIZE_MAP[size as UserAvatarSize];

    return {
      width: predefinedSize,
      height: predefinedSize,
      lineHeight: `${predefinedSize}px`,
      fontSize: `${predefinedSize / AvatarSize.Medium}rem`,
    };
  }, [size]);

  function renderAvatar() {
    return (
      <Avatar sx={avatarSizeStyles} style={{ backgroundColor: stringToColor(username) }}>
        {username
          .split(' ')
          .reduce((result, part) => `${result}${part[0]}`, '')
          .toUpperCase()}
      </Avatar>
    );
  }

  if (isOnline) {
    return (
      <OnlineBadge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} variant="dot">
        {renderAvatar()}
      </OnlineBadge>
    );
  }

  return renderAvatar();
}

export default memo(UserAvatar);
